import type { ImagePreview } from "./display-types";

function normalizeAssetUrl(url: string): string {
  return url.startsWith("//") ? `https:${url}` : url;
}

const ATTRIBUTE_GROUPS = ["unique", "filter", "showcased", "rest"] as const;

export type VariantAttributeRecord = {
  name: string;
  type: string;
  label: string;
  value?: unknown;
  includeInTechSpec?: boolean;
};

export type AttributeDisplay = {
  type: string;
  name: string;
  label: string;
  valueText: string;
  detailText?: string;
  colorHex?: string;
  image?: ImagePreview;
  includeInTechSpec: boolean;
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function imagePreviewFromUnknown(value: unknown): ImagePreview | undefined {
  if (!isPlainObject(value) || typeof value.url !== "string") {
    return undefined;
  }

  return {
    url: normalizeAssetUrl(value.url),
    title: typeof value.title === "string" ? value.title : undefined,
    width: typeof value.width === "number" ? value.width : undefined,
    height: typeof value.height === "number" ? value.height : undefined,
  };
}

export function isVariantAttribute(value: unknown): value is VariantAttributeRecord {
  return (
    isPlainObject(value) &&
    typeof value.name === "string" &&
    typeof value.type === "string" &&
    typeof value.label === "string"
  );
}

export function isVariantAttributesContainer(value: unknown): value is Record<string, unknown> {
  if (!isPlainObject(value)) {
    return false;
  }

  return ATTRIBUTE_GROUPS.some(group => Array.isArray(value[group]));
}

export function flattenAttributes(attributes: Record<string, unknown>, prefix = "attributes"): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const group of ATTRIBUTE_GROUPS) {
    const items = attributes[group];
    if (!Array.isArray(items)) {
      continue;
    }

    for (const item of items) {
      if (!isVariantAttribute(item)) {
        continue;
      }

      result[`${prefix}.${group}.${item.name}`] = item;
    }
  }

  return result;
}

function formatPlainAttributeValue(value: unknown): { valueText: string; detailText?: string } {
  if (!isPlainObject(value) || typeof value.type !== "string") {
    return { valueText: "—" };
  }

  switch (value.type) {
    case "text":
      return { valueText: String(value.label ?? value.data ?? "—") };
    case "number":
      return { valueText: String(value.data ?? "—") };
    case "boolean":
      return { valueText: String(value.label ?? value.data ?? "—") };
    case "enum": {
      const data = value.data;
      if (isPlainObject(data) && typeof data.label === "string") {
        return { valueText: data.label, detailText: typeof data.key === "string" ? data.key : undefined };
      }
      return { valueText: "—" };
    }
    case "enum-set": {
      if (!Array.isArray(value.data)) {
        return { valueText: "—" };
      }

      const labels = value.data.flatMap(item =>
        isPlainObject(item) && typeof item.label === "string" ? [item.label] : [],
      );

      return { valueText: labels.length > 0 ? labels.join(", ") : "—" };
    }
    case "text-set":
      return {
        valueText: Array.isArray(value.data) ? value.data.map(String).join(", ") : "—",
      };
    case "number-set":
      return {
        valueText: Array.isArray(value.data) ? value.data.map(String).join(", ") : "—",
      };
    case "reference": {
      const data = value.data;
      if (isPlainObject(data) && typeof data.id === "string") {
        return { valueText: data.id, detailText: typeof data.typeId === "string" ? data.typeId : undefined };
      }
      return { valueText: "—" };
    }
    default:
      return { valueText: JSON.stringify(value) };
  }
}

export function attributeToDisplay(attribute: VariantAttributeRecord): AttributeDisplay {
  const base = {
    type: attribute.type,
    name: attribute.name,
    label: attribute.label,
    includeInTechSpec: attribute.includeInTechSpec === true,
  };

  switch (attribute.type) {
    case "color": {
      const value = isPlainObject(attribute.value) ? attribute.value : undefined;
      const label =
        typeof value?.label === "string" ? value.label : typeof value?.colorKey === "string" ? value.colorKey : "—";
      const colorHex = typeof value?.hexValue === "string" ? value.hexValue : undefined;
      const colorKey = typeof value?.colorKey === "string" ? value.colorKey : undefined;

      return {
        ...base,
        valueText: label,
        colorHex,
        detailText: colorKey && colorKey !== label ? colorKey : undefined,
      };
    }
    case "reticle": {
      const value = attribute.value === null ? undefined : isPlainObject(attribute.value) ? attribute.value : undefined;
      const reticleKey = typeof value?.reticleKey === "string" ? value.reticleKey : "—";
      const image = value?.image ? imagePreviewFromUnknown(value.image) : undefined;

      return {
        ...base,
        valueText: reticleKey,
        image,
      };
    }
    case "product variant": {
      const value = isPlainObject(attribute.value) ? attribute.value : undefined;
      const name = typeof value?.name === "string" ? value.name : "—";
      const sku = typeof value?.sku === "string" ? value.sku : undefined;

      return {
        ...base,
        valueText: name,
        detailText: sku,
      };
    }
    case "clothing-size":
    case "price-only": {
      const formatted = formatPlainAttributeValue(attribute.value);
      return {
        ...base,
        valueText: formatted.valueText,
        detailText: formatted.detailText,
      };
    }
    case "plain": {
      const formatted = formatPlainAttributeValue(attribute.value);
      return {
        ...base,
        valueText: formatted.valueText,
        detailText: formatted.detailText,
      };
    }
    default:
      return {
        ...base,
        valueText: attribute.value === undefined ? "—" : JSON.stringify(attribute.value),
      };
  }
}

export function normalizeAttributeForComparison(attribute: VariantAttributeRecord): string {
  const display = attributeToDisplay(attribute);

  return [
    display.type,
    display.valueText,
    display.detailText ?? "",
    display.colorHex ?? "",
    display.image?.url ?? "",
    display.includeInTechSpec ? "1" : "0",
  ].join("|");
}
